/**
 * Rate limiting utilities for preventing abuse and ensuring fair usage
 */

import React from 'react';

interface RateLimitEntry {
  count: number;
  resetTime: number;
}

class RateLimiter {
  private limits: Map<string, RateLimitEntry> = new Map();
  private cleanupInterval: NodeJS.Timeout | null = null;

  constructor() {
    // Clean up expired entries every minute
    this.cleanupInterval = setInterval(() => this.cleanup(), 60000);
  }

  /**
   * Check if an action is allowed under rate limits
   * @param key - Unique key for the rate limit (e.g., userId + action)
   * @param maxRequests - Maximum requests allowed
   * @param windowMs - Time window in milliseconds
   * @returns true if action is allowed, false if rate limited
   */
  isAllowed(key: string, maxRequests: number, windowMs: number): boolean {
    const now = Date.now();
    const entry = this.limits.get(key);

    if (!entry || now > entry.resetTime) {
      // No entry or window expired, create new entry
      this.limits.set(key, {
        count: 1,
        resetTime: now + windowMs,
      });
      return true;
    }

    if (entry.count >= maxRequests) {
      // Rate limit exceeded
      return false;
    }

    // Increment count
    entry.count++;
    return true;
  }

  /**
   * Get remaining requests for a key
   * @param key - Unique key for the rate limit
   * @param maxRequests - Maximum requests allowed
   * @returns Number of remaining requests
   */
  getRemainingRequests(key: string, maxRequests: number): number {
    const entry = this.limits.get(key);
    if (!entry || Date.now() > entry.resetTime) {
      return maxRequests;
    }
    return Math.max(0, maxRequests - entry.count);
  }

  /**
   * Get time until rate limit resets
   * @param key - Unique key for the rate limit
   * @returns Milliseconds until reset, or 0 if no limit
   */
  getResetTime(key: string): number {
    const entry = this.limits.get(key);
    if (!entry || Date.now() > entry.resetTime) {
      return 0;
    }
    return entry.resetTime - Date.now();
  }

  /**
   * Reset rate limit for a specific key
   * @param key - Unique key to reset
   */
  reset(key: string): void {
    this.limits.delete(key);
  }

  /**
   * Clean up expired entries
   */
  private cleanup(): void {
    const now = Date.now();
    for (const [key, entry] of this.limits.entries()) {
      if (now > entry.resetTime) {
        this.limits.delete(key);
      }
    }
  }

  /**
   * Destroy the rate limiter and clean up resources
   */
  destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    this.limits.clear();
  }
}

// Global rate limiter instance
let globalRateLimiter = new RateLimiter();

// Setup cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    globalRateLimiter.destroy();
    // Create a new instance so the module continues to work after cleanup
    globalRateLimiter = new RateLimiter();
  });
}

/**
 * Rate limit configuration
 */
export interface RateLimitConfig {
  maxRequests: number;
  windowMs: number;
  keyPrefix?: string;
  onRateLimitExceeded?: (key: string) => void;
}

/**
 * Default rate limit configurations
 */
export const RateLimitPresets = {
  // API calls: 100 requests per minute
  API_CALLS: { maxRequests: 100, windowMs: 60000 },
  
  // Form submissions: 5 per minute
  FORM_SUBMIT: { maxRequests: 5, windowMs: 60000 },
  
  // File uploads: 10 per hour
  FILE_UPLOAD: { maxRequests: 10, windowMs: 3600000 },
  
  // Search queries: 30 per minute
  SEARCH: { maxRequests: 30, windowMs: 60000 },
  
  // Authentication attempts: 5 per 15 minutes
  AUTH: { maxRequests: 5, windowMs: 900000 },
} as const;

/**
 * Check if an action is rate limited
 * @param userId - User identifier
 * @param action - Action being performed
 * @param config - Rate limit configuration
 * @returns true if action is allowed, false if rate limited
 */
export function checkRateLimit(
  userId: string,
  action: string,
  config: RateLimitConfig
): boolean {
  const key = `${config.keyPrefix || ''}${userId}:${action}`;
  const allowed = globalRateLimiter.isAllowed(key, config.maxRequests, config.windowMs);
  
  if (!allowed && config.onRateLimitExceeded) {
    config.onRateLimitExceeded(key);
  }
  
  return allowed;
}

/**
 * React hook for rate limiting
 * @param action - Action being performed
 * @param config - Rate limit configuration
 * @returns Object with rate limit state and check function
 */
export function useRateLimit(action: string, config: RateLimitConfig) {
  const [isRateLimited, setIsRateLimited] = React.useState(false);
  const [remainingRequests, setRemainingRequests] = React.useState(config.maxRequests);
  const [resetTime, setResetTime] = React.useState(0);

  const checkLimit = React.useCallback((userId: string) => {
    const key = `${config.keyPrefix || ''}${userId}:${action}`;
    const allowed = globalRateLimiter.isAllowed(key, config.maxRequests, config.windowMs);
    
    setIsRateLimited(!allowed);
    setRemainingRequests(globalRateLimiter.getRemainingRequests(key, config.maxRequests));
    setResetTime(globalRateLimiter.getResetTime(key));
    
    if (!allowed && config.onRateLimitExceeded) {
      config.onRateLimitExceeded(key);
    }
    
    return allowed;
  }, [action, config]);

  return {
    isRateLimited,
    remainingRequests,
    resetTime,
    checkLimit,
  };
}

/**
 * Decorator for rate limiting class methods
 * @param config - Rate limit configuration
 */
export function RateLimit(config: RateLimitConfig) {
  return function (_target: unknown, propertyKey: string, descriptor: PropertyDescriptor) {
    const originalMethod = descriptor.value;
    
    descriptor.value = function (...args: unknown[]) {
      // Extract userId from first argument or use 'anonymous'
      const userId = (args[0] as { userId?: string })?.userId || 'anonymous';
      
      if (!checkRateLimit(userId, propertyKey, config)) {
        throw new Error(`Rate limit exceeded for ${propertyKey}`);
      }
      
      return originalMethod.apply(this, args);
    };
    
    return descriptor;
  };
}

// Export global instance methods for direct use
export const resetRateLimit = (key: string) => globalRateLimiter.reset(key);
export const getRemainingRequests = (key: string, maxRequests: number) => 
  globalRateLimiter.getRemainingRequests(key, maxRequests);
export const getResetTime = (key: string) => globalRateLimiter.getResetTime(key);